import Footer from "../Footer";
import Navbar from "../Navbar";
import { Link } from "react-router-dom";
import SEO from "../SEO";

const PrivacyPolicy: React.FC = () => {
  const policySections = [
    {
      title: "1. Information We Collect",
      list: [
        "Personal Identification: Full Name, Email, Phone Number, Date of Birth, Address, PAN, Aadhaar, Employment Information, Bank Account Details, and Financial Data.",
        "Financial Information: Documents such as Bank Statements, Salary Slips, and Consent for Credit Checks are required for loan applications.",
        "Transactional Information: Details about your loans, repayment schedules, and transaction history are recorded.",
      ],
    },
    {
      title: "2. How We Use Your Information",
      list: [
        "Loan Processing: We assess your loan eligibility, process applications, and manage repayments.",
        "Service Improvement: We enhance our platform based on user feedback and interactions.",
        "Communication: We send notifications and respond to inquiries related to our services.",
        "Compliance: We comply with legal requirements like KYC and AML regulations.",
      ],
    },
    {
      title: "3. Sharing Your Information",
      list: [
        "Authorized Third Parties: We work with Credit Bureaus, Payment Gateways, and Verification Agencies to process and verify your information.",
        "Legal Authorities: We may disclose your information as required by the law, such as for regulatory reporting or fraud investigations.",
        `Parent Company: We may share your data with our parent company, ${import.meta.env.VITE_COMPANY_NBFC}, for loan processing.`,
      ],
    },
    {
      title: "4. Data Security",
      content:
        "We employ encryption, secure servers, and multi-factor authentication to safeguard your data. However, no system is 100% secure, and we encourage users to proceed with caution.",
    },
    {
      title: "5. Cookies and Tracking",
      content:
        "We use cookies and tracking technologies to enhance your browsing experience. You can control cookies through browser settings.",
    },
    {
      title: "6. Your Rights",
      content: (
        <>
          You have the right to access, correct, delete, or request portability of
          your data. You can also opt out of marketing communications at any time.
          <br />
          <br />
          For requesting deletion of your personal data, please refer to our{" "}
          <Link
            to="/account-delete-policy"
            className="text-secondary hover:underline font-black"
          >
            User Data Deletion Policy
          </Link>
          .
        </>
      ),
    },

    {
      title: "7. Retention of Information",
      content:
        "We retain your information as long as it is necessary for business or legal reasons, and securely delete it when it is no longer needed.",
    },
    {
      title: "8. Third-Party Links",
      content:
        "Our platform may link to third-party websites. We are not responsible for their privacy practices.",
    },
    {
      title: "9. Children’s Privacy",
      content:
        "We do not knowingly collect data from children under 18. If we find any, we will delete it.",
    },
    {
      title: "10. Policy Updates",
      content:
        "We may update this policy periodically. Check this page for any changes.",
    },
    {
      title: "11. Contact Us",
      list: [
        `Email: ${import.meta.env.VITE_COMPANY_EMAIL}`,
        `Phone: ${import.meta.env.VITE_COMPANY_PHONE}`,
        `Address: ${import.meta.env.VITE_COMPANY_ADDRESS}`,
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary font-mono selection:bg-secondary selection:text-white ">
      <SEO
        title="Privacy Policy"
        description={`Our commitment to protecting your digital footprint. Learn how ${import.meta.env.VITE_COMPANY_NAME} handles personal identification, financial data, and security.`}
        keywords={`privacy policy, data protection, secure lending, ${import.meta.env.VITE_COMPANY_NAME} privacy`}
      />
      <Navbar />

      {/* Official Registry Hero */}
      <section className="w-full bg-text-primary py-32 md:py-48 text-center px-6 relative overflow-hidden border-b-8 border-secondary mb-24">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{ backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-4 px-6 py-2 bg-white/5 border border-white/10 text-secondary text-[10px] font-black uppercase tracking-[0.5em] mb-12">
            <div className="w-2 h-2 bg-secondary animate-pulse" />
            Privacy_Archive_Protocol_v5.4
          </div>
          <h1 className="text-5xl md:text-[6rem] font-black tracking-tighter leading-[0.85] mb-12 uppercase text-white italic">
            Privacy <span className="text-secondary not-italic">Policy.</span>
          </h1>
          <p className="text-white/60 text-lg md:text-2xl max-w-3xl mx-auto font-bold leading-relaxed border-y border-white/10 py-10 uppercase tracking-tight">
            Institutional standards for data protection. <br />
            <span className="text-white">Ensuring transparency in information management at {import.meta.env.VITE_COMPANY_NAME}.</span>
          </p>
        </div>
      </section>

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 pb-40">
        <div className="border-t-4 border-text-primary">
          {policySections.map((section, idx) => (
            <div key={idx} className="border-b border-text-primary/10 py-16 group">
              <div className="flex flex-col gap-10">
                <div className="flex items-start gap-8">
                  <span className="text-secondary font-black text-xs pt-1">SEC_00{idx + 1}</span>
                  <h2 className="text-3xl md:text-5xl font-black text-text-primary leading-tight uppercase tracking-tighter italic">
                    {section.title.split('. ')[1] || section.title}
                  </h2>
                </div>

                <div className="pl-16 md:pl-24 space-y-8">
                  {section.content && (
                    <div className="text-text-secondary font-bold text-lg leading-relaxed uppercase tracking-tight border-l-4 border-text-primary/5 pl-8 italic">
                      {section.content}
                    </div>
                  )}
                  {section.list && (
                    <ul className="space-y-6">
                      {section.list.map((li, i) => (
                        <li key={i} className="flex items-start gap-4 text-text-primary font-bold text-base leading-relaxed">
                          <div className="w-2 h-2 bg-secondary mt-2 shrink-0" />
                          <span className="uppercase tracking-tight">{li}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
